import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Train, Plane, Hospital, GraduationCap, TreePine, Clapperboard, Building2, Route, TrainFront } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import { LANDING } from '@/constants/testIds';

const icons = [MapPin, Route, Building2, TrainFront, Hospital, GraduationCap, TreePine, Clapperboard, Plane, Train];

export default function LocationMatrix() {
  const { lang } = useLanguage();
  const t = translations[lang].location;

  return (
    <section id="location" data-testid={LANDING.locationMatrix} className="py-16 sm:py-24 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-16"
        >
          <span className="text-[#D48C57] text-xs font-medium tracking-[0.2em] uppercase">
            {t.sectionTag}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2520] mt-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-[#5C6A61] text-base mt-3 max-w-2xl">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Location Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {t.items.map((item, i) => {
            const Icon = icons[i] || MapPin;
            return (
              <motion.div
                key={i}
                data-testid={`${LANDING.locationCard}-${i}`}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ delay: Math.min(i * 0.05, 0.3), duration: 0.4 }}
                className="group relative bg-white rounded-xl p-5 border border-[#E5DFD3] hover:border-[#2C4C3B]/20 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                {/* Number */}
                <span className="absolute top-3 right-3 font-mono text-[10px] text-[#E5DFD3] font-bold">
                  {String(i + 1).padStart(2, '0')}
                </span>

                {/* Icon */}
                <div className="w-10 h-10 rounded-lg bg-[#F4F1EA] flex items-center justify-center mb-4 group-hover:bg-[#2C4C3B] transition-colors duration-300">
                  <Icon className="w-5 h-5 text-[#2C4C3B] group-hover:text-white transition-colors duration-300" />
                </div>

                {/* Content */}
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A61] mb-1">{item.metric}</p>
                <p className="font-serif text-sm font-bold text-[#1A2520] leading-snug">{item.value}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
